
import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ChargingStation } from '@/types/task';
import { cn } from '@/lib/utils';

interface MapProps {
  stations: ChargingStation[];
  onStationSelect?: (station: ChargingStation) => void;
  className?: string;
}

const Map = ({ stations, onStationSelect, className }: MapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapboxToken] = useState('pk.eyJ1IjoiYWRkb25lbmVyZ3kiLCJhIjoiY205bmNpc3lrMGhoODJscTVpaXR0bG5yeCJ9.0YdvvkcUOHBepJfuACKgHw');

  useEffect(() => {
    if (!mapContainer.current || !mapboxToken) return;

    // Clean up existing map if any
    if (map.current) {
      markers.current.forEach(marker => marker.remove());
      map.current.remove();
    }

    // Initialize map
    mapboxgl.accessToken = mapboxToken;
    
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-122.4194, 37.7749], // San Francisco coordinates
      zoom: 11
    });

    // Add navigation controls
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Wait for map to load before allowing markers to be added
    mapInstance.on('load', () => {
      console.log('Map loaded successfully');
      map.current = mapInstance;
      setMapLoaded(true);
    });

    // Handle error
    mapInstance.on('error', (e) => {
      console.error('Mapbox error:', e);
    });

    return () => {
      markers.current.forEach(marker => marker.remove());
      mapInstance.remove();
    };
  }, [mapboxToken]);

  // Add markers in a separate effect that depends on mapLoaded state
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    console.log('Adding markers to map');
    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add markers for each station
    stations.forEach(station => {
      if (station.coordinates) {
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.width = '32px';
        el.style.height = '32px';
        el.style.backgroundImage = station.isHomeCharger 
          ? 'url(https://cdn-icons-png.flaticon.com/512/1476/1476071.png)' 
          : 'url(https://docs.mapbox.com/mapbox-gl-js/assets/custom_marker.png)';
        el.style.backgroundSize = 'cover';
        el.style.cursor = 'pointer';

        const popupContent = `
          <h3 class="text-base font-semibold">${station.title}</h3>
          <p class="text-sm">${station.chargerType} - ${station.powerOutput}kW</p>
          <p class="text-sm">$${station.price}/kWh</p>
          ${station.isHomeCharger ? '<p class="text-xs font-medium text-blue-500">Home Charger</p>' : ''}
          ${station.availableHours ? `<p class="text-xs">Available: ${station.availableHours}</p>` : ''}
        `;

        try {
          const marker = new mapboxgl.Marker(el)
            .setLngLat([station.coordinates.longitude, station.coordinates.latitude])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 })
                .setHTML(popupContent)
            )
            .addTo(map.current!);

          el.addEventListener('click', () => {
            if (onStationSelect) {
              onStationSelect(station);
            }
          });

          markers.current.push(marker);
        } catch (error) {
          console.error('Error adding marker:', error);
        }
      }
    });
  }, [stations, onStationSelect, mapLoaded]);

  return (
    <div className={cn("w-full h-[400px] rounded-lg overflow-hidden shadow-lg", className)}>
      <div ref={mapContainer} className="w-full h-full" />
    </div>
  );
};

export default Map;
